import type * as React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@cloudscape-design/collection-hooks';
import Box from '@cloudscape-design/components/box';
import PropertyFilter, {
  type PropertyFilterProps,
} from '@cloudscape-design/components/property-filter';
import Table, { type TableProps } from '@cloudscape-design/components/table';

/**
 * Tabela padrão do admin com a caixa de filtros embutida (Cloudscape
 * `PropertyFilter`). O filtro roda no cliente, sobre as linhas já carregadas.
 *
 * Origem: #907 — 2026-10-09 16:12 +0200. É um port do `FilterTable` do CRM
 * (`apps/crm/src/components/FilterTable.tsx`, #775).
 *
 * Por quê: lista pequena que já chega inteira do Firestore (catálogo de
 * add-ons, por exemplo) não precisa de callable de busca nem de tradutor de
 * tokens. O `useCollection` faz filtro, contagem, opções de valor e ordenação
 * sozinho. A cópia é intencional: o admin bane `@etiquetachef/ui-shared`, e o
 * CRM é outro app. Se precisar mudar o comportamento, mude nos dois.
 * Para lista grande ou paginada no servidor, o modelo é o `TenantList`.
 *
 * Contrato: cada `filteringProperties[].key` tem de ser um campo **string** do
 * item, porque é o que o `useCollection` compara. Valor derivado para exibição
 * (rótulo traduzido, lista de países) vai como campo extra na linha. O texto
 * livre procura em todas as propriedades. A ordenação é por `sortingField` de
 * cada coluna. `filteringOptions` é opcional: sem ele, o `useCollection`
 * sugere cada valor inteiro da propriedade. Passe a lista quando um campo junta
 * vários valores (países `"BR, IT"`). Quando presente, ela **substitui** todas
 * as sugestões, então tem de cobrir todas as propriedades.
 *
 * Usado por: `AddonsPage` (`/billing/addons`, admin).
 *
 * Dicas: diferente do CRM, os textos de interface do `PropertyFilter`
 * (operadores, Aplicar, Limpar…) vêm do `I18nProvider` do Cloudscape em
 * `main.tsx`. Aqui só ficam placeholder, contagem e "nenhum resultado"
 * (`common.filterTable.*`).
 *
 * Issues: #907 (port para o admin), #775 (origem no CRM).
 */
export function FilterTable<T>(props: {
  items: readonly T[];
  columnDefinitions: readonly TableProps.ColumnDefinition<T>[];
  filteringProperties: readonly PropertyFilterProps.FilteringProperty[];
  filteringOptions?: readonly PropertyFilterProps.FilteringOption[];
  header?: ReactNode;
  empty?: ReactNode;
  loading?: boolean;
  variant?: TableProps['variant'];
  trackBy?: TableProps<T>['trackBy'];
}): React.JSX.Element {
  const { t } = useTranslation();
  const noMatch = (
    <Box textAlign="center" color="text-status-inactive" padding="m">
      {t('common.filterTable.noMatch')}
    </Box>
  );
  const { items, collectionProps, propertyFilterProps, filteredItemsCount } = useCollection<T>(
    props.items,
    {
      sorting: {},
      propertyFiltering: {
        filteringProperties: [...props.filteringProperties],
        // Auditoria 2026-10-09 16:20 +0200 (#907): sem lista externa, a coluna
        // de países sugeria "BR, IT" como valor único em vez de BR e IT.
        ...(props.filteringOptions ? { filteringOptions: [...props.filteringOptions] } : {}),
        empty: props.empty ?? noMatch,
        noMatch,
      },
    },
  );
  const extra: Partial<TableProps<T>> = {};
  if (props.trackBy) extra.trackBy = props.trackBy;
  return (
    <Table<T>
      {...collectionProps}
      {...extra}
      variant={props.variant ?? 'container'}
      header={props.header}
      items={items}
      loading={props.loading ?? false}
      loadingText={t('common.loading')}
      columnDefinitions={props.columnDefinitions}
      wrapLines={false}
      filter={
        <PropertyFilter
          {...propertyFilterProps}
          i18nStrings={{
            filteringAriaLabel: t('common.filterTable.placeholder'),
            filteringPlaceholder: t('common.filterTable.placeholder'),
            enteredTextLabel: (text: string): string =>
              t('common.filterTable.searchFor', { text }),
          }}
          countText={t('common.filterTable.matches', { count: filteredItemsCount ?? 0 })}
          expandToViewport
        />
      }
    />
  );
}
